/**
 * @upsetjs/addons
 * https://github.com/upsetjs/upsetjs
 *
 * Copyright (c) 2022 Samuel Gratzl <sam@sgratzl.com>
 */

import React from 'react';
import { getDefaultTheme } from '@upsetjs/react';
import { normalize, denormalize, boxplot, categoricalHistogram } from '@upsetjs/math';

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

/**
 * @upsetjs/addons
 * https://github.com/upsetjs/upsetjs
 *
 * Copyright (c) 2021 Samuel Gratzl <sam@sgratzl.com>
 */
function round2(v) {
    return Math.round(v * 100) / 100;
}

/**
 * @upsetjs/addons
 * https://github.com/upsetjs/upsetjs
 *
 * Copyright (c) 2021 Samuel Gratzl <sam@sgratzl.com>
 */
function simpleScale(domain, range, orient = 'horizontal') {
    const n = normalize(domain);
    const dnDomain = denormalize(domain);
    const dn = denormalize(range);
    const f = (v) => dn(n(v));
    const defaultTicks = orient === 'horizontal' ? 5 : 7;
    f.ticks = (count = defaultTicks) => Array(count)
        .fill(0)
        .map((_, i) => {
        const v = dnDomain(i / (count - 1));
        return {
            value: v,
            label: v.toLocaleString(undefined, { maximumFractionDigits: 2 }),
        };
    });
    f.tickFormat = () => (v) => v.toFixed(2);
    return f;
}
const defaultFormatter = (v) => v.toFixed(2);
function toString$1(b, nf) {
    return `Min: ${nf(b.min)}, 25% Quantile: ${nf(b.q1)}, Median: ${nf(b.median)}, 75% Quantile: ${nf(b.q3)}, Max: ${nf(b.max)}`;
}
const Boxplot = (p) => {
    const { theme = 'light', mode = 'normal', boxStyle, lineStyle, outlierStyle, margin = 0, boxPadding: bpp = 0.1, outlierRadius = 3, numberFormat: nf = defaultFormatter } = p, options = __rest(p, ["theme", "mode", "boxStyle", "lineStyle", "outlierStyle", "margin", "boxPadding", "outlierRadius", "numberFormat"]);
    const b = Array.isArray(p.values) ? boxplot(p.values, options) : p.values;
    if (Number.isNaN(b.median)) {
        return React.createElement("g", null);
    }
    const hor = p.orient !== 'vertical';
    const n = normalize([p.min, p.max]);
    const dn = denormalize([0, hor ? p.width : p.height]);
    const scale = (v) => round2(dn(n(v)));
    const s = {
        max: scale(b.max),
        avg: scale(b.mean),
        med: scale(b.median),
        min: scale(b.min),
        q1: scale(b.q1),
        q3: scale(b.q3),
        wh: scale(b.whiskerHigh),
        wl: scale(b.whiskerLow),
    };
    const title = p.tooltips !== false && React.createElement("title", null, toString$1(b, nf));
    const inner = getDefaultTheme(theme).notMemberColor;
    const styles = {
        box: Object.assign({ fill: inner }, boxStyle),
        line: Object.assign({ fill: 'none', stroke: theme === 'light' ? 'black' : '#cccccc' }, lineStyle),
        outlier: Object.assign({ fill: inner }, outlierStyle),
    };
    if (hor) {
        const c = p.height / 2;
        const bp = round2(p.height * bpp) + margin;
        const hp = p.height - bp;
        const w1 = `M${s.wl},${margin} l0,${p.height - margin * 2} M${s.wl},${c} L${s.q1},${c}`;
        const w2 = `M${s.q3},${c} L${s.wh},${c} M${s.wh},${margin} L${s.wh},${p.height - margin}`;
        const box = `M${s.q1},${bp} L${s.q3},${bp} L${s.q3},${hp} L${s.q1},${hp} L${s.q1},${bp} M${s.med},${bp} l0,${hp - bp}`;
        const path = React.createElement("path", { d: `${w1} ${w2} ${box}`, style: styles.line });
        if (mode === 'indicator') {
            return path;
        }
        return (React.createElement("g", null,
            title,
            mode === 'normal' && (React.createElement("rect", { x: s.q1, y: bp, width: s.q3 - s.q1, height: p.height - 2 * bp, style: styles.box })),
            path,
            b.outlier.map((o) => (React.createElement("circle", { key: o, r: outlierRadius, cy: c, cx: scale(o), style: styles.outlier },
                React.createElement("title", null,
                    "$",
                    nf(o)))))));
    }
    {
        const c = p.width / 2;
        const bp = round2(p.width * bpp) + margin;
        const wp = p.width - bp;
        const w1 = `M${margin},${s.wl} l${p.width - 2 * margin},0 M${c},${s.wl} L${c},${s.q1}`;
        const w2 = `M${c},${s.q3} L${c},${s.wh} M${margin},${s.wh} L${p.width - margin},${s.wh}`;
        const box = `M${bp},${s.q1} L${bp},${s.q3} l${wp - bp},0 L${wp},${s.q1} L${bp},${s.q1} M${bp},${s.med} l${wp - bp},0`;
        const path = React.createElement("path", { d: `${w1} ${w2} ${box}`, style: styles.line });
        if (mode === 'indicator') {
            return path;
        }
        return (React.createElement("g", null,
            title,
            mode === 'normal' && React.createElement("rect", { y: s.q1, x: bp, height: s.q3 - s.q1, width: p.width - 2 * bp, style: styles.box }),
            path,
            b.outlier.map((o) => (React.createElement("circle", { key: o, r: outlierRadius, cx: c, cy: scale(o), style: styles.outlier },
                React.createElement("title", null,
                    "$",
                    nf(o)))))));
    }
};
const BoxplotMemo = /*!#__PURE__*/ React.memo(Boxplot);
/**
 * generates a boxplot addon to render box plots as UpSet.js addon for aggregated set data
 * @param prop accessor or name of the property within the element
 * @param elems list of elements or their minimum / maximum value for specifying the data domain
 * @param options additional options
 */
function boxplotAddon(prop, elems, _a = {}) {
    var { size = 100, position, name = prop.toString() } = _a, extras = __rest(_a, ["size", "position", "name"]);
    const acc = typeof prop === 'function' ? prop : (v) => v[prop];
    let min = Number.POSITIVE_INFINITY;
    let max = Number.NEGATIVE_INFINITY;
    if (Array.isArray(elems)) {
        elems.forEach((elem) => {
            const v = acc(elem);
            if (v == null || Number.isNaN(v)) {
                return;
            }
            if (v < min) {
                min = v;
            }
            if (v > max) {
                max = v;
            }
        });
    }
    else {
        const d = elems;
        min = d.min;
        max = d.max;
    }
    const scale = simpleScale([min, max], [0, size], extras.orient);
    return {
        name,
        position,
        size,
        scale,
        createOnHandlerData: (set) => {
            const b = boxplot(set.elems.map(acc), extras);
            return {
                id: 'boxplot',
                name,
                value: Object.assign(Object.assign(Object.assign({}, b), { toString() {
                        var _a;
                        return toString$1(this, (_a = extras.numberFormat) !== null && _a !== void 0 ? _a : defaultFormatter);
                    } }), b),
            };
        },
        render: ({ width, height, set, theme }) => {
            const values = set.elems.map(acc);
            return (React.createElement(BoxplotMemo, Object.assign({ values: values, width: width, height: height, min: min, max: max, theme: theme }, extras)));
        },
        renderSelection: ({ width, height, overlap, selectionColor, theme }) => {
            if (overlap == null || overlap.length === 0) {
                return null;
            }
            const values = overlap.map(acc);
            return (React.createElement(BoxplotMemo, Object.assign({ values: values, width: width, height: height, min: min, max: max, mode: "box", lineStyle: { stroke: selectionColor, strokeWidth: 2 }, outlierStyle: { fill: selectionColor }, theme: theme }, extras)));
        },
        renderQuery: ({ width, height, overlap, query, secondary, index, theme }) => {
            if (overlap == null || overlap.length === 0) {
                return null;
            }
            const values = overlap.map(acc);
            return (React.createElement(BoxplotMemo, Object.assign({ values: values, width: width, height: height, min: min, max: max, mode: secondary ? 'indicator' : 'box', margin: secondary ? index + 2 : 0, lineStyle: { stroke: query.color, strokeWidth: secondary ? 1 : 2 }, outlierStyle: { fill: query.color }, theme: theme }, extras)));
        },
    };
}
/**
 * generates a boxplot addon to render box plots as UpSet.js addon for aggregated set data
 * @param acc accessor
 * @param elems list of elements or their minimum / maximum value for specifying the data domain
 * @param options additional options
 */
function boxplotAggregatedAddon(acc, domain, _a = {}) {
    var { size = 100, position, name = 'BoxPlot' } = _a, extras = __rest(_a, ["size", "position", "name"]);
    const min = domain.min;
    const max = domain.max;
    const scale = simpleScale([min, max], [0, size], extras.orient);
    return {
        name,
        position,
        size,
        scale,
        createOnHandlerData: (set) => {
            const b = acc(set.elems);
            b.toString = function () {
                var _a;
                return toString$1(this, (_a = extras.numberFormat) !== null && _a !== void 0 ? _a : defaultFormatter);
            };
            return {
                id: 'boxplot',
                name,
                value: b,
            };
        },
        render: ({ width, height, set, theme }) => {
            const values = acc(set.elems);
            return (React.createElement(BoxplotMemo, Object.assign({ values: values, width: width, height: height, min: min, max: max, theme: theme }, extras)));
        },
        renderSelection: ({ width, height, overlap, selectionColor, theme }) => {
            if (overlap == null || overlap.length === 0) {
                return null;
            }
            const values = acc(overlap);
            return (React.createElement(BoxplotMemo, Object.assign({ values: values, width: width, height: height, min: min, max: max, mode: "box", lineStyle: { stroke: selectionColor, strokeWidth: 2 }, outlierStyle: { fill: selectionColor }, theme: theme }, extras)));
        },
        renderQuery: ({ width, height, overlap, query, secondary, index, theme }) => {
            if (overlap == null || overlap.length === 0) {
                return null;
            }
            const values = acc(overlap);
            return (React.createElement(BoxplotMemo, Object.assign({ values: values, width: width, height: height, min: min, max: max, mode: secondary ? 'indicator' : 'box', margin: secondary ? index + 2 : 0, lineStyle: { stroke: query.color, strokeWidth: secondary ? 1 : 2 }, outlierStyle: { fill: query.color }, theme: theme }, extras)));
        },
    };
}

/**
 * @upsetjs/addons
 * https://github.com/upsetjs/upsetjs
 *
 * Copyright (c) 2021 Samuel Gratzl <sam@sgratzl.com>
 */
function isGiven(p) {
    return Array.isArray(p.bins);
}
function toString(cat) {
    return cat.map((bin) => `${bin.label}: ${bin.count}`).join(', ');
}
const Categorical = (p) => {
    var _a, _b;
    const { margin = 0, rectStyle = {} } = p;
    const bins = isGiven(p) ? p.bins : categoricalHistogram(p.values, p.categories, p.base, p.theme === 'dark');
    const n = normalize([
        0,
        isGiven(p) ? p.bins.reduce((acc, v) => acc + v.count, 0) : (_b = (_a = p.base) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : p.values.length,
    ]);
    const hor = p.orient !== 'vertical';
    const dn = denormalize([0, hor ? p.width : p.height]);
    const scale = (v) => dn(n(v));
    if (hor) {
        return (React.createElement("g", null, bins.map((bin) => (React.createElement("rect", { key: bin.value, x: scale(bin.acc), width: scale(bin.count), y: margin, height: p.height - 2 * margin, style: Object.assign({ fill: bin.color }, rectStyle) }, p.tooltips !== false && React.createElement("title", null, `${bin.label}: ${bin.count}`))))));
    }
    return (React.createElement("g", null, bins.map((bin) => (React.createElement("rect", { key: bin.value, y: scale(bin.acc), height: scale(bin.count), x: margin, width: p.width - 2 * margin, style: Object.assign({ fill: bin.color }, rectStyle) }, p.tooltips !== false && React.createElement("title", null, `${bin.label}: ${bin.count}`))))));
};
const CategoricalMemo = /*!#__PURE__*/ React.memo(Categorical);
const lightOverlap = 'rgba(255,255,255,0.2)';
const darkOverlap = 'rgba(0,0,0,0.1)';
/**
 * generates a categorical addon to render distributions as UpSet.js addon for aggregated set data
 * @param prop accessor or name of the property within the element
 * @param elems list of elements or their categories
 * @param options additional options
 */
function categoricalAddon(prop, elems, _a = {}) {
    var { size = 100, position, name = prop.toString() } = _a, extras = __rest(_a, ["size", "position", "name"]);
    const acc = typeof prop === 'function' ? prop : (v) => v[prop];
    let categories = [];
    if (Array.isArray(elems)) {
        const cats = new Set();
        elems.forEach((elem) => {
            const v = acc(elem);
            if (v == null) {
                return;
            }
            cats.add(v.toString());
        });
        categories = Array.from(cats).sort();
    }
    else {
        categories = elems.categories;
    }
    return {
        name,
        position,
        size,
        createOnHandlerData: (set) => {
            const b = categoricalHistogram(set.elems.map(acc), categories, undefined, extras.theme === 'dark');
            b.toString = function () {
                return toString(this);
            };
            return {
                id: 'categorical',
                name,
                value: b,
            };
        },
        render: ({ width, height, set, theme }) => {
            const values = set.elems.map(acc);
            return (React.createElement(CategoricalMemo, Object.assign({ values: values, categories: categories, width: width, height: height, theme: theme }, extras)));
        },
        renderSelection: ({ width, height, set, theme, overlap, selectionColor }) => {
            if (overlap == null || overlap.length === 0) {
                return null;
            }
            const base = set.elems.map(acc);
            const values = overlap.map(acc);
            return (React.createElement(CategoricalMemo, Object.assign({ values: values, base: base, categories: categories, width: width, height: height, theme: theme, rectStyle: { stroke: selectionColor, strokeWidth: 2, fill: theme === 'light' ? darkOverlap : lightOverlap } }, extras)));
        },
        renderQuery: ({ width, height, overlap, set, query, secondary, index, theme }) => {
            if (overlap == null || overlap.length === 0) {
                return null;
            }
            const base = set.elems.map(acc);
            const values = overlap.map(acc);
            return (React.createElement(CategoricalMemo, Object.assign({ values: values, base: base, categories: categories, width: width, height: height, margin: secondary ? index + 2 : 0, rectStyle: { stroke: query.color, fill: theme === 'light' ? darkOverlap : lightOverlap }, theme: theme }, extras)));
        },
    };
}
/**
 * generates a categorical addon to render distributions as UpSet.js addon for aggregated set data
 * @param prop accessor or name of the property within the element
 * @param elems list of elements or their categories
 * @param options additional options
 */
function categoricalAggregatedAddon(acc, _a = {}) {
    var { size = 100, position, name = 'Histogram' } = _a, extras = __rest(_a, ["size", "position", "name"]);
    return {
        name,
        position,
        size,
        createOnHandlerData: (set) => {
            const b = acc(set.elems);
            return {
                id: 'categorical',
                name,
                value: Object.assign(Object.assign(Object.assign({}, b), { toString() {
                        return toString(this);
                    } }), b),
            };
        },
        render: ({ width, height, set, theme }) => {
            const values = acc(set.elems);
            return React.createElement(CategoricalMemo, Object.assign({ bins: values, width: width, height: height, theme: theme }, extras));
        },
        renderSelection: ({ width, height, theme, overlap, selectionColor }) => {
            if (overlap == null || overlap.length === 0) {
                return null;
            }
            const values = acc(overlap);
            return (React.createElement(CategoricalMemo, Object.assign({ bins: values, width: width, height: height, theme: theme, rectStyle: { stroke: selectionColor, strokeWidth: 2, fill: theme === 'light' ? darkOverlap : lightOverlap } }, extras)));
        },
        renderQuery: ({ width, height, overlap, query, secondary, index, theme }) => {
            if (overlap == null || overlap.length === 0) {
                return null;
            }
            const values = acc(overlap);
            return (React.createElement(CategoricalMemo, Object.assign({ bins: values, width: width, height: height, margin: secondary ? index + 2 : 0, rectStyle: { stroke: query.color, fill: theme === 'light' ? darkOverlap : lightOverlap }, theme: theme }, extras)));
        },
    };
}

export { Boxplot, Categorical, boxplotAddon, boxplotAggregatedAddon, categoricalAddon, categoricalAggregatedAddon, simpleScale };
//# sourceMappingURL=index.js.map
